import { useQuery } from '@apollo/client'
import {
  Dialog,
  DialogMaxWidth,
  DialogVariant,
  Grid,
  Select,
  SelectVariant,
  Text,
  TextSize
} from '@focaldata/cin-ui-components'
import React, { useEffect, useMemo, useState } from 'react'
import {
  QUESTIONNAIRE,
  QuestionnaireData
} from '../../../../data/gql/questionnaire/queries'
import {
  AudienceData,
  GET_AUDIENCES
} from '../../../../data/gql/questionnaire/queries/audiences'
import { LoggerErrorType } from '../../../../data/logger'
import useDownloadSurveyDoc from '../../../../hooks/useDownloadSurveyDoc'
import { useOptionalWaveId } from '../../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../../hooks/useProjectId'
import { useSurveyId } from '../../../../hooks/useSurveyId'
import { flattenEntries } from '../../../../modules/Questionnaire/Questionnaire.utils'
import { languagesInUse } from '../../../../modules/Translations/lib/languagesInUse'
import {
  captureApolloError,
  getLanguageLabel
} from '../../../../utils/HelperFunctions'

interface Props {
  open: boolean
  onClose: () => void
  // The language the caller is currently showing (the results pages); ignored
  // when it has no stored translations.
  defaultLanguage?: string
}

// English is always the master / source language (the Translations editor
// authors against it regardless of country), so the export overlay treats
// every other language as a translation to apply.
const MASTER_LANGUAGE = 'en'

const ExportByLanguageDialog: React.FC<Props> = ({
  open,
  onClose,
  defaultLanguage
}) => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  // Only an explicit pick is held in state: `defaultLanguage` is applied below
  // instead of seeded here, so a caller whose language resolves late (the
  // results pages await fieldwork) can't overwrite a choice already made.
  const [picked, setPicked] = useState<string>()
  const { exportSurvey } = useDownloadSurveyDoc()

  // Reset the picker every time the dialog opens. The dialog is always mounted
  // (toggled via `open`), so without this the previous selection would persist
  // across cancel + reopen.
  useEffect(() => {
    if (open) setPicked(undefined)
  }, [open])

  // Read the shared questionnaire + custom-audience data (cache-first, so this
  // reuses what the editor already loaded) to discover which languages actually
  // have saved translations. This is the same source the Translations sidebar
  // uses, and — crucially — it doesn't depend on the current survey's
  // `languageAllocations`. In multi-market the export runs off the master
  // survey, whose own allocations are empty (they live on the per-market
  // surveys), so the old allocation-based list only ever showed English even
  // when translations existed on the shared questionnaire.
  //
  // Skipped while closed: the dialog is mounted on the results pages too, where
  // nothing else needs this data.
  const {
    data: questionnaireData,
    loading: questionnaireLoading,
    error: questionnaireError
  } = useQuery<QuestionnaireData>(QUESTIONNAIRE, {
    context: { clientName: 'questionnaire' },
    variables: { projectId, surveyId, waveId },
    skip: !open,
    onError: (error) =>
      captureApolloError(
        LoggerErrorType.ApolloQuery,
        'draftQuestionnaireV2',
        error
      )
  })
  const {
    data: audienceData,
    loading: audienceLoading,
    error: audienceError
  } = useQuery<AudienceData>(GET_AUDIENCES, {
    context: { clientName: 'questionnaire' },
    variables: { projectId, surveyId, waveId },
    skip: !open,
    onError: (error) =>
      captureApolloError(LoggerErrorType.ApolloQuery, 'draftAudienceV2', error)
  })
  const optionsLoading = questionnaireLoading || audienceLoading
  // On failure the list collapses to English alone, so exporting would ship
  // master text as if it were the requested language. Block it instead.
  const optionsFailed = Boolean(questionnaireError || audienceError)

  // English (master) first, then every language that has at least one stored
  // translation, alphabetical by display label. Falling back to master text is
  // handled per-field by the export overlay, so only languages actually worth
  // exporting are offered.
  const options = useMemo(() => {
    const entries = flattenEntries(
      questionnaireData?.draftQuestionnaire.entries ?? []
    )
    const audienceMembers = audienceData?.draftAudience.members ?? []
    const translated = Array.from(languagesInUse(entries, audienceMembers))
      .filter((code) => code !== MASTER_LANGUAGE)
      .sort((a, b) => getLanguageLabel(a).localeCompare(getLanguageLabel(b)))

    return [MASTER_LANGUAGE, ...translated].map((code) => ({
      id: code,
      name:
        code === MASTER_LANGUAGE
          ? `${getLanguageLabel(code)} (master)`
          : getLanguageLabel(code),
      value: code
    }))
  }, [
    questionnaireData?.draftQuestionnaire.entries,
    audienceData?.draftAudience.members
  ])

  // A caller's `defaultLanguage` is a fieldwork language, which need not have
  // translations, so fall back to master rather than render an empty picker.
  // Export stays disabled until the options are known, or this would silently
  // fall back on a language that is still loading.
  //
  // Fieldwork codes can carry a region ("de-DE" — useResultsLanguages keeps
  // whatever the locale declares) while translations are stored against the bare
  // language ("de"), so an exact miss retries on the primary subtag.
  const preferred = picked ?? defaultLanguage ?? MASTER_LANGUAGE
  const primarySubtag = (code: string) => code.split('-')[0]
  const selectedLanguage =
    (
      options.find((option) => option.value === preferred) ??
      options.find(
        (option) => primarySubtag(option.value) === primarySubtag(preferred)
      )
    )?.value ?? MASTER_LANGUAGE

  const getCaption = () => {
    if (optionsFailed) {
      return 'Couldn’t load the available languages. Close the dialog and try again.'
    }
    if (optionsLoading) {
      return 'Loading languages…'
    }
    return 'Selecting a non-master language exports translated text where available, falling back to master text otherwise.'
  }
  const caption = getCaption()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Export survey by language"
      variant={DialogVariant.DoubleButton}
      maxWidth={DialogMaxWidth.Sm}
      primaryButtonText="Export"
      primaryButtonDisabled={optionsLoading || optionsFailed}
      primaryButtonClick={() => {
        const code =
          selectedLanguage === MASTER_LANGUAGE ? undefined : selectedLanguage
        exportSurvey(code)
        onClose()
      }}
      secondaryButtonText="Cancel"
      secondaryButtonClick={onClose}
    >
      <Grid container direction="column" spacing={2} style={{ padding: 16 }}>
        <Grid item>
          <Text size={TextSize.s}>{caption}</Text>
        </Grid>
        <Grid item>
          <Select
            variant={SelectVariant.Outlined}
            options={options}
            optionValue={selectedLanguage}
            ariaLabel="Export language"
            onChange={(e) => setPicked(e.target.value as string)}
          />
        </Grid>
      </Grid>
    </Dialog>
  )
}

export default ExportByLanguageDialog
